import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { UserService } from '../../core/services/user.service';
import { User, UpdatePasswordRequest, UserProfile, UpdateProfileRequest, SubUser } from '../../core/models/user.model';

@Component({
  selector: 'app-users',
  imports: [CommonModule, FormsModule, Sidebar],
  templateUrl: './users.html',
  styleUrl: './users.scss',
})
export class UsersPage implements OnInit {
  users: User[] = [];
  filteredUsers: User[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  // Filters
  searchTerm: string = '';
  selectedRole: string = '';

  // Pagination
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  totalPages: number = 0;
  visiblePages: number[] = [];

  // Password update sidebar
  showPasswordSidebar: boolean = false;
  isUpdatingPassword: boolean = false;
  selectedUser: User | null = null;
  newPassword: string = '';
  confirmPassword: string = '';
  passwordError: string = '';

  constructor(
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.loadUsers();
  }

  loadUsers() {
    this.isLoading = true;
    this.errorMessage = '';
    this.userService.getAllUsers(this.searchTerm, this.selectedRole, this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.users = response.data.users;
          this.filteredUsers = response.data.users;
          this.currentPage = response.data.currentPage;
          this.pageSize = response.data.pageSize;
          this.totalCount = response.data.totalCount;
          this.totalPages = response.data.totalPages;
          this.calculatePagination();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error.error?.message || 'Failed to load users';
        console.error('Error loading users:', error);
      }
    });
  }

  calculatePagination() {
    if (this.totalPages === 0) this.totalPages = 1;
    this.visiblePages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  applyFilters() {
    this.currentPage = 1;
    this.loadUsers();
  }

  clearFilters() {
    this.searchTerm = '';
    this.selectedRole = '';
    this.currentPage = 1;
    this.loadUsers();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadUsers();
  }

  getRoleBadgeClass(role: string): string {
    switch (role.toLowerCase()) {
      case 'owner': return 'role-owner';
      case 'manager': return 'role-manager';
      case 'user': return 'role-user';
      default: return 'role-user';
    }
  }

  openPasswordSidebar(user: User) {
    this.selectedUser = user;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
    this.showPasswordSidebar = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closePasswordSidebar() {
    this.showPasswordSidebar = false;
    this.selectedUser = null;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
  }

  updatePassword() {
    this.passwordError = '';
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.selectedUser) return;

    if (!this.newPassword || this.newPassword.length < 6) {
      this.passwordError = 'Password must be at least 6 characters long';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'Passwords do not match';
      return;
    }

    this.isUpdatingPassword = true;

    const request: UpdatePasswordRequest = {
      userId: this.selectedUser.id,
      newPassword: this.newPassword
    };

    this.userService.updateUserPassword(request).subscribe({
      next: (response) => {
        this.isUpdatingPassword = false;
        if (response.success) {
          this.successMessage = 'Password updated successfully';
          this.closePasswordSidebar();
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.isUpdatingPassword = false;
        this.passwordError = error.error?.message || 'Failed to update password';
        console.error('Error updating password:', error);
      }
    });
  }

  // Profile modal
  showProfileModal = false;
  isLoadingProfile = false;
  isSavingProfile = false;
  profileError = '';
  profileUserId = '';
  profileForm: UpdateProfileRequest = {};

  static readonly BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  static readonly GENDERS = ['Male', 'Female'];
  get bloodTypes() { return UsersPage.BLOOD_TYPES; }
  get genders() { return UsersPage.GENDERS; }

  openProfileModal(user: User) {
    this.profileUserId = user.id;
    this.showProfileModal = true;
    this.isLoadingProfile = true;
    this.profileError = '';
    this.profileForm = {};

    this.userService.getUserProfile(user.id).subscribe({
      next: (res) => {
        this.isLoadingProfile = false;
        if (res.success && res.data) {
          const p = res.data;
          this.profileForm = {
            name: p.name,
            gender: p.gender,
            dateOfBirth: p.dateOfBirth ? p.dateOfBirth.split('T')[0] : null,
            weight: p.weight,
            height: p.height,
            bloodType: p.bloodType,
            allergies: p.allergies,
            medicalConditions: p.medicalConditions,
            medications: p.medications,
            emergencyContactName: p.emergencyContactName,
            emergencyContactPhone: p.emergencyContactPhone,
            nationalId: p.nationalId,
          };
        }
      },
      error: (err) => {
        this.isLoadingProfile = false;
        this.profileError = err.error?.message || 'Failed to load profile';
      }
    });
  }

  closeProfileModal() {
    this.showProfileModal = false;
    this.profileError = '';
  }

  saveProfile() {
    this.isSavingProfile = true;
    this.profileError = '';

    this.userService.updateUserProfile(this.profileUserId, this.profileForm).subscribe({
      next: (res) => {
        this.isSavingProfile = false;
        if (res.success) {
          this.closeProfileModal();
          this.successMessage = 'Profile updated successfully';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (err) => {
        this.isSavingProfile = false;
        this.profileError = err.error?.message || 'Failed to update profile';
      }
    });
  }

  // Family members modal
  showFamilyModal = false;
  isLoadingFamily = false;
  familyError = '';
  familyUser: User | null = null;
  familyMembers: SubUser[] = [];

  openFamilyModal(user: User) {
    this.familyUser = user;
    this.showFamilyModal = true;
    this.isLoadingFamily = true;
    this.familyError = '';
    this.familyMembers = [];

    this.userService.getSubUsers(user.id).subscribe({
      next: (res) => {
        this.isLoadingFamily = false;
        if (res.success && res.data) {
          this.familyMembers = res.data;
        }
      },
      error: (err) => {
        this.isLoadingFamily = false;
        this.familyError = err.error?.message || 'Failed to load family members';
      }
    });
  }

  closeFamilyModal() {
    this.showFamilyModal = false;
    this.familyError = '';
    this.familyUser = null;
  }

  verifyUserPhone(user: User) {
    if (!confirm(`Are you sure you want to manually verify ${user.name}'s phone number?`)) return;

    this.userService.verifyUserPhone(user.id).subscribe({
      next: (response) => {
        if (response.success) {
          user.isPhoneVerified = true;
          this.successMessage = `${user.name}'s phone has been verified successfully.`;
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Failed to verify phone';
        setTimeout(() => this.errorMessage = '', 5000);
      }
    });
  }

  formatDate(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}
